import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const PortfolioPage = () => {
  const heroRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const propertiesRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const upcomingRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const marketsRef = useScrollAnimation<HTMLDivElement>('fade-in-up');
  const ctaRef = useScrollAnimation<HTMLDivElement>('fade-in-up');

  return (
    <div className="min-h-screen bg-[#F5F5F3]">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center bg-[#1F2A44] mt-20">
        <div className="absolute inset-0 opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)' }}></div>
        </div>
        <div ref={heroRef} className="relative max-w-[1440px] mx-auto px-20 w-full text-center">
          <div className="w-16 h-0.5 bg-[#B89C4B] mx-auto mb-6"></div>
          <h1
            className="text-[56px] text-white leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Portfolio
          </h1>
          <p
            className="text-[18px] text-white/85 max-w-[640px] mx-auto"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Current properties and upcoming opportunities across our target Midwest markets
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-white border-b border-[#D9D9D6]">
        <div className="max-w-[1440px] mx-auto px-20 py-6">
          <div className="flex justify-between items-center">
            <h2
              className="text-[15px] text-[#1F2A44] font-semibold"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Current Properties
            </h2>
            <div className="flex items-center space-x-4">
              <button className="w-8 h-8 flex items-center justify-center text-[#1F2A44] hover:text-[#B89C4B] transition-colors cursor-pointer">
                <i className="ri-grid-fill text-xl"></i>
              </button>
              <button className="w-8 h-8 flex items-center justify-center text-[#2E2E2E]/50 hover:text-[#B89C4B] transition-colors cursor-pointer">
                <i className="ri-list-check text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Property Grid Section */}
      <section className="bg-[#F5F5F3] py-24">
        <div className="max-w-[1440px] mx-auto px-20">
          <div ref={propertiesRef} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: 'Riverside Commons',
                location: 'Indianapolis, IN',
                units: '42 Units',
                type: 'Garden Style',
                description: 'Value-add multifamily property with strong fundamentals and renovation upside in growing submarket. Property features spacious units and opportunity for common area improvements.',
                image: 'https://public.readdy.ai/ai/img_res/6e8ea77b0314171eccedce7bc5a79367.jpg',
              },
              {
                name: 'Oakwood Terrace',
                location: 'Columbus, OH',
                units: '36 Units',
                type: 'Low-Rise',
                description: 'Well-located asset with opportunity for interior upgrades and operational improvements to drive NOI growth. Strong rental demand in established neighborhood.',
                image: 'https://readdy.ai/api/search-image?query=quality%20apartment%20building%20with%20modern%20facade%20well-maintained%20grounds%20professional%20property%20management%20architectural%20photography%20real%20estate%20investment%20midwest%20market&width=600&height=400&seq=port002&orientation=landscape',
              },
              {
                name: 'Heritage Park',
                location: 'Grand Rapids, MI',
                units: '48 Units',
                type: 'Garden Style',
                description: 'Stabilized property in high-demand area with potential for rent optimization and amenity enhancements. Excellent location near employment centers.',
                image: 'https://readdy.ai/api/search-image?query=established%20multifamily%20residential%20property%20with%20classic%20architecture%20mature%20landscaping%20quality%20construction%20professional%20real%20estate%20photography%20investment%20grade%20asset&width=600&height=400&seq=port003&orientation=landscape',
              },
              {
                name: 'Maple Ridge',
                location: 'Fort Wayne, IN',
                units: '32 Units',
                type: 'Low-Rise',
                description: 'Attractive property with solid occupancy and opportunity for strategic capital improvements. Located in stable residential area with strong schools.',
                image: 'https://readdy.ai/api/search-image?query=residential%20multifamily%20apartment%20building%20with%20traditional%20design%20clean%20exterior%20professional%20landscaping%20investment%20property%20photography%20midwest%20real%20estate&width=600&height=400&seq=port004&orientation=landscape',
              },
              {
                name: 'Brookside Village',
                location: 'Dayton, OH',
                units: '40 Units',
                type: 'Garden Style',
                description: 'Value-add opportunity with below-market rents and deferred maintenance. Clear path to operational improvements and rent growth.',
                image: 'https://readdy.ai/api/search-image?query=multifamily%20apartment%20complex%20with%20garden%20style%20layout%20spacious%20grounds%20quality%20construction%20professional%20property%20photography%20real%20estate%20investment%20asset&width=600&height=400&seq=port005&orientation=landscape',
              },
              {
                name: 'Sunset Pointe',
                location: 'South Bend, IN',
                units: '28 Units',
                type: 'Low-Rise',
                description: 'Smaller property with strong cash flow and opportunity for unit upgrades. Excellent tenant retention and stable neighborhood.',
                image: 'https://readdy.ai/api/search-image?query=small%20multifamily%20property%20with%20quality%20construction%20well-maintained%20exterior%20professional%20management%20architectural%20photography%20midwest%20investment%20real%20estate&width=600&height=400&seq=port006&orientation=landscape',
              },
            ].map((property, index) => (
              <div
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                style={{
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.name}
                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <div className="p-9">
                  <h3
                    className="text-[24px] text-[#1F2A44] mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
                  >
                    {property.name}
                  </h3>
                  <p
                    className="text-[14px] text-[#2E2E2E] mb-2"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className="ri-map-pin-line text-[#B89C4B] mr-2"></i>
                    {property.location}
                  </p>
                  <p
                    className="text-[14px] text-[#2E2E2E] mb-4"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {property.units} <span className="text-[#B89C4B] mx-2">•</span> {property.type}
                  </p>
                  <p
                    className="text-[15px] text-[#2E2E2E] leading-relaxed mb-6"
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    {property.description}
                  </p>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span
                      className="text-[14px] text-[#B89C4B] font-medium hover:underline cursor-pointer"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      View Details →
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Opportunities Section */}
      <section className="bg-white py-28 border-t border-[#D9D9D6]">
        <div ref={upcomingRef} className="max-w-[1440px] mx-auto px-20 text-center">
          <h2
            className="text-[38px] text-[#1F2A44] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Upcoming Opportunities
          </h2>
          <p
            className="text-[16px] text-[#2E2E2E] max-w-[680px] mx-auto mb-8 leading-relaxed"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            We are actively evaluating new acquisitions across our target markets. Qualified investors can request our current pipeline overview and learn about upcoming investment opportunities.
          </p>
          <Link
            to="/investors"
            className="inline-block px-12 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Request Overview
          </Link>
        </div>
      </section>

      {/* Market Focus Section */}
      <section className="bg-[#F5F5F3] py-32">
        <div className="max-w-[1440px] mx-auto px-20">
          <div className="text-center mb-16">
            <h2
              className="text-[42px] text-[#1F2A44] mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
            >
              Our Target Markets
            </h2>
            <p
              className="text-[17px] text-[#2E2E2E] max-w-[800px] mx-auto"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              We focus on Midwest markets with strong employment fundamentals, stable population growth, and attractive multifamily investment opportunities
            </p>
          </div>

          <div ref={marketsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { city: 'Indianapolis', state: 'Indiana' },
              { city: 'Columbus', state: 'Ohio' },
              { city: 'Grand Rapids', state: 'Michigan' },
              { city: 'Fort Wayne', state: 'Indiana' },
              { city: 'Dayton', state: 'Ohio' },
              { city: 'South Bend', state: 'Indiana' },
              { city: 'Toledo', state: 'Ohio' },
              { city: 'Lansing', state: 'Michigan' },
            ].map((market, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg border-l-4 border-[#B89C4B] shadow-sm hover:shadow-md transition-all duration-300"
                style={{
                  animationDelay: `${index * 0.08}s`,
                }}
              >
                <h3
                  className="text-[17px] text-[#1F2A44] font-semibold mb-1"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {market.city}
                </h3>
                <p
                  className="text-[14px] text-[#2E2E2E]"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {market.state}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white py-24">
        <div ref={ctaRef} className="max-w-[1440px] mx-auto px-20 text-center">
          <h2
            className="text-[38px] text-[#1F2A44] mb-5"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
          >
            Learn More About Our Approach
          </h2>
          <p
            className="text-[16px] text-[#2E2E2E] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Explore our investment strategy and value creation process
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/strategy"
              className="px-10 py-3.5 bg-[#B89C4B] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Our Strategy
            </Link>
            <Link
              to="/contact"
              className="px-10 py-3.5 border-2 border-[#1F2A44] text-[#1F2A44] text-[15px] font-medium rounded transition-all duration-300 hover:bg-[#1F2A44] hover:text-white whitespace-nowrap cursor-pointer"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PortfolioPage;